import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  CheckCircle, Package, Envelope, Truck, CircleNotch, Warning, Copy, ArrowSquareOut, Timer, Star, GoogleLogo
} from '@phosphor-icons/react';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';
import { MrgHeader as SharedMagereHeader } from './MrgHeader';
import { MrgFooter as SharedMagereFooter } from './MrgFooter';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrderConfirmationPage = ({ storeSlug: propStoreSlug }) => {
  const { storeSlug: paramStoreSlug } = useParams();
  const storeSlug = propStoreSlug || paramStoreSlug || 'mrg';
  const standalone = !!propStoreSlug || !paramStoreSlug;
  const [searchParams] = useSearchParams();
  const orderNumber = searchParams.get('order');
  const sessionId = searchParams.get('session_id');
  const trackingCode = searchParams.get('tracking');
  const paypalCapture = searchParams.get('paypal_capture');
  const paypalOrderId = searchParams.get('token'); // PayPal adds ?token=PAYPAL_ORDER_ID
  const orderId = searchParams.get('oid');

  const { clearCart } = useCart();
  const [store, setStore] = useState(null);
  const [preparationInfo, setPreparationInfo] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(sessionId ? 'checking' : paypalCapture ? 'checking' : 'success');
  const [polled, setPolled] = useState(false);
  const [capturedPaypal, setCapturedPaypal] = useState(false);

  const getLink = (path) => standalone ? path : `/store/${storeSlug}${path}`;

  useEffect(() => {
    axios.get(`${API_URL}/api/s/${storeSlug}`).then(r => {
      setStore(r.data);
      const prepConfig = r.data?.preparation_config;
      if (prepConfig) {
        setPreparationInfo(prepConfig);
      }
    }).catch(() => {});
  }, [storeSlug]);

  // Clear cart on mount (user returned from Stripe or COD)
  useEffect(() => { clearCart(); }, []); // eslint-disable-line

  // Poll Stripe payment status
  useEffect(() => {
    if (!sessionId || polled) return;

    let attempts = 0;
    const maxAttempts = 6;
    const interval = 2000;

    const poll = async () => {
      try {
        const resp = await axios.get(`${API_URL}/api/s/${storeSlug}/checkout/status/${sessionId}`);
        if (resp.data.payment_status === 'paid') {
          setPaymentStatus('success');
          setPolled(true);
          return;
        }
        if (resp.data.status === 'expired') {
          setPaymentStatus('failed');
          setPolled(true);
          return;
        }
        attempts++;
        if (attempts >= maxAttempts) {
          setPaymentStatus('success'); // Assume success after timeout
          setPolled(true);
          return;
        }
        setTimeout(poll, interval);
      } catch {
        attempts++;
        if (attempts >= maxAttempts) {
          setPaymentStatus('success');
          setPolled(true);
        } else {
          setTimeout(poll, interval);
        }
      }
    };

    setPaymentStatus('checking');
    poll();
  }, [sessionId, storeSlug, polled]);

  // Capture PayPal payment on return
  useEffect(() => {
    if (!paypalCapture || !paypalOrderId || capturedPaypal) return;
    
    const capturePaypal = async () => {
      setPaymentStatus('checking');
      try {
        const resp = await axios.post(`${API_URL}/api/s/${storeSlug}/checkout/paypal/capture`, {
          paypal_order_id: paypalOrderId,
          order_id: orderId,
        });
        if (resp.data.paid) {
          setPaymentStatus('success');
        } else {
          setPaymentStatus('failed');
        }
      } catch (err) {
        console.error('PayPal capture error:', err);
        setPaymentStatus('failed');
      }
      setCapturedPaypal(true);
    };

    capturePaypal();
  }, [paypalCapture, paypalOrderId, storeSlug, orderId, capturedPaypal]);

  const colors = { primary: store?.identity?.primary_color || '#c2185b' };
  const isMagere = store?.identity?.theme_template === 'magere' || standalone;

  const copyTracking = () => {
    if (trackingCode) {
      navigator.clipboard.writeText(trackingCode);
      toast.success('Code de suivi copié !');
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f5f5] flex flex-col" data-testid="order-confirmation" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Jost:wght@300;400;500;600;700;900&display=swap" rel="stylesheet" />
      {isMagere && <SharedMagereHeader store={store} standalone={standalone} />}
      {isMagere && <div className="h-[50px]" />}

      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <Card className="max-w-lg w-full border-0 shadow-lg">
          <CardContent className="p-8 md:p-12 text-center">
            {paymentStatus === 'checking' ? (
              <>
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center">
                  <CircleNotch size={48} className="animate-spin" style={{ color: colors.primary }} />
                </div>
                <h1 className="text-2xl font-bold mb-2">Vérification du paiement...</h1>
                <p className="text-slate-500 text-sm">Veuillez patienter, nous vérifions votre paiement</p>
              </>
            ) : paymentStatus === 'failed' ? (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
                  <Warning size={36} className="text-red-600" weight="fill" />
                </div>
                <h1 className="text-2xl font-bold mb-2">Paiement non abouti</h1>
                <p className="text-slate-500 text-sm mb-6">Votre paiement n'a pas pu être traité. Votre commande est en attente.</p>
                <Link to={getLink('/')}>
                  <Button style={{ backgroundColor: colors.primary }} className="text-white">Retour à la boutique</Button>
                </Link>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5">
                  <CheckCircle size={36} className="text-emerald-600" weight="fill" />
                </div>

                <h1 className="text-2xl font-bold mb-1">Merci pour votre commande !</h1>
                <p className="text-slate-500 text-sm mb-6">Votre commande a été confirmée</p>

                {orderNumber && (
                  <div className="bg-slate-50 rounded-xl p-4 mb-4">
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-bold">Commande</p>
                    <p className="text-xl font-bold mt-1" data-testid="confirmation-order-number">{orderNumber}</p>
                  </div>
                )}

                {trackingCode && (
                  <div className="bg-slate-50 rounded-xl p-4 mb-4" data-testid="confirmation-tracking">
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-bold">Code de suivi</p>
                    <div className="flex items-center justify-center gap-2 mt-1">
                      <p className="text-lg font-bold font-mono" style={{ color: colors.primary }}>{trackingCode}</p>
                      <button onClick={copyTracking} className="text-slate-400 hover:text-slate-700 transition-colors" data-testid="copy-tracking-btn">
                        <Copy size={16} />
                      </button>
                    </div>
                    <Link to={`/suivi/${trackingCode}`} className="inline-flex items-center gap-1 text-xs mt-2 hover:underline" style={{ color: colors.primary }} data-testid="track-order-link">
                      Suivre ma commande <ArrowSquareOut size={12} />
                    </Link>
                  </div>
                )}

                {/* Preparation time info */}
                {preparationInfo && (
                  <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4 text-left" data-testid="preparation-delay-info">
                    <Timer size={24} className="text-amber-600 flex-shrink-0" weight="fill" />
                    <div>
                      <p className="text-sm font-semibold text-amber-800">Delai de preparation</p>
                      <p className="text-xs text-amber-700">
                        {(preparationInfo.message || '').replace('{days}', String(preparationInfo.preparation_days || 1))}
                      </p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 mb-6">
                  <div className="p-3 rounded-xl bg-blue-50/80">
                    <Envelope size={20} className="text-blue-600 mx-auto mb-1.5" />
                    <p className="text-xs font-medium">Confirmation email</p>
                    <p className="text-[10px] text-slate-400">Quand SMTP est configuré</p>
                  </div>
                  <div className="p-3 rounded-xl bg-purple-50/80">
                    <Truck size={20} className="text-purple-600 mx-auto mb-1.5" />
                    <p className="text-xs font-medium">Suivi livraison</p>
                    <p className="text-[10px] text-slate-400">Notifications par email</p>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <Link to={getLink('/')}>
                    <Button className="w-full text-white" style={{ backgroundColor: colors.primary }}>
                      Continuer mes achats
                    </Button>
                  </Link>
                  {trackingCode && (
                    <Link to={`/suivi/${trackingCode}`}>
                      <Button variant="outline" className="w-full">
                        <Package size={16} className="mr-2" /> Suivre ma commande
                      </Button>
                    </Link>
                  )}
                  {store?.settings?.google_review_url && (
                    <a href={store.settings.google_review_url} target="_blank" rel="noopener noreferrer" data-testid="google-review-link">
                      <Button variant="outline" className="w-full border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 hover:text-amber-900">
                        <Star size={16} weight="fill" className="mr-2 text-amber-500" /> Laisser un avis Google
                      </Button>
                    </a>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {isMagere && <SharedMagereFooter store={store} standalone={standalone} />}
    </div>
  );
};

export default OrderConfirmationPage;
